/**
 * useAllWorkflows is responsible for fetching all the workflows and rendering them in a list.
 */

import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Workflow } from "../../../../types/types"
import { FileExplorerDataProps } from "../../WorkflowFileExplorer.types"
import FolderIcon from "../../../../media/FolderIcon"
import { deleteWorkflow, getWorkflows } from "../../../../service/workflowService"
import AllWorkflowsSection from "./AllWorkflowsSection"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { showErrorToast, showLoadingToast, showSuccessToast } from "../../../../utils/utils"
import { Id, toast } from "react-toastify"

const useAllWorkflows = (props: FileExplorerDataProps) => {

  const { limit, skip, onError, searchQuery } = props

  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [showMoveWorkflow, setShowMoveWorkflow] = useState<boolean>(false)
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingToastId, setLoadingToastId] = useState<Id|undefined>(undefined)
  const [error, setError] = useState<Error|undefined>(undefined)
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow|undefined>(undefined)
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false)
  
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && loadingToastId !== undefined) {
      toast.done(loadingToastId)
    }
  }, [isLoading, loadingToastId])

  const handleError = useCallback((error: Error) => {
    onError(error)
    setIsLoading(false)
    setShowMoveWorkflow(false)
    setError(error)
    setCanLoadMore(false)
  }, [onError])

  /**
   * Fetch all workflows within given limit and offset
   */
  const onRefresh = useCallback(async () => {
    setIsLoading(true)
    const response = await getWorkflows(limit, skip)

    if (response instanceof Error) {
      handleError(response)
      return
    }
    const fetchedWorkflows = response as Workflow[]
    setCanLoadMore(fetchedWorkflows.length >= limit)
    setWorkflows(fetchedWorkflows)
    setIsLoading(false)
  }, [limit, skip, handleError])

  useEffect(() => {
    onRefresh()
    setError(undefined)
  }, [onRefresh])

  const onWorkflowClick = (workflow: Workflow) => {
    navigate("/workflow", {
      state: workflow
    })
  }

  const onDeleteWorkflow = async () => {
    if (selectedWorkflow === undefined) {
      showErrorToast("Failed to delete workflow.")
      return
    }
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Deleting ${selectedWorkflow.pipe_id}.`))
    const response = await deleteWorkflow(selectedWorkflow.id)
    setIsLoading(false)
    
    if (response instanceof Error) {
      handleError(response)
      return
    }
    showSuccessToast("Workflow deleted")
    setShowConfirmDelete(false)
    onRefresh()
  }

  const onShowMoveWorkflow = async () => {
    setShowMoveWorkflow(true)
  }

  const onHideMoveWorkflow = () => {
    setShowMoveWorkflow(false)
  }

  return [{
    fileExplorerOption: {
      option: {
        icon: <FolderIcon />,
        label: "All Workflows",
      },
      component: <AllWorkflowsSection 
        workflows={workflows.filter(workflow => workflow.pipe_id.toLowerCase().includes(searchQuery.toLowerCase()))}
        selectedWorkflow={selectedWorkflow}
        onSelectWorkflow={setSelectedWorkflow}
        onOpenWorkflow={onWorkflowClick}
        onDeleteWorkflow={onDeleteWorkflow}
        onMoveWorkflow={() => setShowMoveWorkflow(false)} 
        showMoveWorkflow={showMoveWorkflow} 
        onHideMoveWorkflow={onHideMoveWorkflow}
        onShowMoveWorkflow={onShowMoveWorkflow}
        error={error}
        showConfirmDelete={showConfirmDelete}
        onShowDeleteConfirmation={setShowConfirmDelete}
        onError={handleError}
      />,
      onRefresh: onRefresh,
      filterOptions: <p>Filter</p>,
      sortOptions: <p>Sort</p>,
      canLoadMore: canLoadMore,
      isLoading: isLoading,
      loadingView: <>
        <div className="all-workflows-header-container content-loading-view">
          <p className="workflows-result-header">PIPE ID</p>
          <p className="workflows-result-header">ASSET TEMPERATURE</p>
          <p className="workflows-result-header">DATE</p>
          <p className="workflows-result-header">WORKFLOW ANOMALIES</p>
          <p className="workflows-result-header"></p>
          {[...Array(10)].map((_, i) => (
            <>
              <SkeletonLoadingView />
              <SkeletonLoadingView />
              <SkeletonLoadingView />
              <SkeletonLoadingView style={{gridColumn: "span 2", backgroundColor: "blue"}} />
            </>
          ))}
        </div>
      </>
    }
  }]

}

export default useAllWorkflows